/**
 * Admin Dashboard Team Management Hooks
 *
 * TanStack Query wrappers around the `/v1/admin/dashboard/team` API.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  deleteTeamUser,
  disableTeamUser,
  enableTeamUser,
  fetchTeamUsers,
  inviteTeamUser,
  resendTeamInvite,
  updateTeamUserRole,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  InviteTeamUserRequest,
  InviteTeamUserResponse,
  ListTeamUsersParams,
  ListTeamUsersResponse,
  SimpleMessageResponse,
  UpdateTeamRoleRequest,
  UpdateTeamRoleResponse,
} from '@/types/team';

const TEAM_QUERY_KEY = ['team'] as const;

function requireToken(token: string | null): string {
  if (!token) {
    throw new Error('No ID token available. Please log in again.');
  }
  return token;
}

export function useTeamUsers(
  params: ListTeamUsersParams
): UseQueryResult<ListTeamUsersResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: [...TEAM_QUERY_KEY, params],
    queryFn: () => fetchTeamUsers(params, requireToken(getIdToken())),
    staleTime: 15000,
    gcTime: 300000,
    retry: 1,
  });
}

export function useInviteTeamUser(): UseMutationResult<
  InviteTeamUserResponse,
  Error,
  InviteTeamUserRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: InviteTeamUserRequest) => inviteTeamUser(body, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}

export function useResendTeamInvite(): UseMutationResult<SimpleMessageResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) => resendTeamInvite(username, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}

export function useDisableTeamUser(): UseMutationResult<SimpleMessageResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) => disableTeamUser(username, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}

export function useEnableTeamUser(): UseMutationResult<SimpleMessageResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) => enableTeamUser(username, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}

export function useDeleteTeamUser(): UseMutationResult<SimpleMessageResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) => deleteTeamUser(username, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}

export function useUpdateTeamUserRole(): UseMutationResult<
  UpdateTeamRoleResponse,
  Error,
  { username: string; body: UpdateTeamRoleRequest }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, body }) =>
      updateTeamUserRole(username, body, requireToken(getIdToken())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
  });
}
